import React, { useEffect, useRef, useState } from 'react'
import { Grid, FormSectionTitle, makeStyles } from '@paintscout/ui'
import type { MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { useClientOptions, Editor, useUser } from '@paintscout/ui-business'
import { getObjectLabels } from '@paintscout/util/builder'
import { useQuote } from '../../../context'

const useStyles = makeStyles<Theme, NotesViewProps>(
  (theme) => ({
    root: {},
    editor: {
      '& #menu-bar': {
        top: 83,
        [theme.breakpoints.down('sm')]: {
          top: 68
        }
      }
    }
  }),
  { name: 'NotesView' }
)

export interface NotesViewProps {
  classes?: StyleClasses<typeof useStyles>
}

function NotesView(props: NotesViewProps) {
  const classes = useStyles(props)
  const { quote, isEditable, isNoteEditable, updateQuote } = useQuote()
  const { isTeam } = useUser()
  const { options } = useClientOptions()
  const estimatorNotes = quote?.estimatorNotes
  const objectLabels = getObjectLabels({ options })

  // Track the last content the editor wrote so we can distinguish internal
  // edits (user typing) from external updates (AI patch). Only external
  // changes should force a remount — internal changes must not or the editor
  // loses cursor position on every keystroke.
  const lastInternalContent = useRef(estimatorNotes)
  const editorRef = useRef(null)
  const [editorKey, setEditorKey] = useState(`notesView-editor-${isEditable}`)

  // Remount when edit mode toggles (original behaviour).
  useEffect(() => {
    setEditorKey(`notesView-editor-${isEditable}-${Date.now()}`)
  }, [isEditable])

  // Remount only when content changes from outside (AI patch), not from user typing.
  useEffect(() => {
    if (estimatorNotes !== lastInternalContent.current) {
      lastInternalContent.current = estimatorNotes
      setEditorKey(`notesView-editor-${isEditable}-${Date.now()}`)
    }
  }, [estimatorNotes])

  // Commit the estimator notes into quote state on blur rather than on every keystroke. Pushing
  // the whole quote through updateQuote per keystroke re-rendered the entire editor tree several
  // times a second and caused the "typing in bursts / cursor freeze" lag (#21233). There is no
  // timer autosave, and clicking Save / navigating away blurs the editor first, so a blur commit
  // reliably lands before a save. Mirrors the ref-on-confirm pattern used by EditCrewNoteDialog.
  const commitNotes = () => {
    const value = editorRef.current?.getHTML?.()
    // Bail if the editor ref isn't available — committing '' here would wipe existing notes.
    // A mounted, focused editor always returns its HTML, so this only skips spurious commits.
    if (value == null || quote.estimatorNotes === value) {
      return
    }
    lastInternalContent.current = value
    updateQuote({ quote: { ...quote, estimatorNotes: value } })
  }

  // Only commit when focus actually leaves the editor (incl. its toolbar) — not when moving
  // between the contenteditable and a formatting button inside it.
  const handleBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget as Node | null)) {
      return
    }
    commitNotes()
  }

  return (
    <Grid container spacing={0}>
      <Grid item xs={12}>
        <FormSectionTitle
          title={'Estimator Notes'}
          variant="h2"
          subTitle={`These notes are a "scratch pad" for estimators and won't appear anywhere else on the ${objectLabels.quote.value.toLowerCase()}`}
        />
      </Grid>
      <Grid item xs={12} onBlur={handleBlur}>
        <Editor
          ref={editorRef}
          classes={{ root: classes.editor }}
          content={estimatorNotes}
          fullWidth={true}
          disabled={(!isEditable && !isNoteEditable) || isTeam}
          /* this is nasty, but the slate editor was 0% interested
          in becoming not-disabled when changed to editable */
          key={editorKey}
        />
      </Grid>
    </Grid>
  )
}

export default NotesView
